import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { showConfirmModal } from "../lib/custom_confirm"
import { setButtonLoading } from "../lib/button_loading"

// Staff Find an Agent workspace on Settings → Trust Center (#18684): runs the
// Action Shelf and Next-Step Card buttons. Each button carries the JSON
// endpoint it calls (the SAME Api::V1::React::Users::TrustCenter* endpoints
// the User panel's 10DLC rows call) and its confirm copy.
//
//   confirm first (MODAL-6) → every button disabled, the clicked one busy with
//   its label unchanged (BUTTON-8) → POST → on success the page reloads so the
//   staff card shows the new state; the result message survives the reload.
//
// A button the server rendered disabled (with its reason, INTERACTION-5)
// stays disabled after a failed request.
//
// Upload Opt-In Screenshot (#18750, `data-upload="true"`) picks the image
// first, then confirms naming the file, then posts it as multipart under
// `opt_in_screenshot` — the field the agent's own Evidence Kit upload uses.

// Scoped to the page's path, so a message can only ever show on the agent's
// workspace that stored it.
const FLASH_PREFIX = "trust-center-staff-actions:flash:"
const flashKey = () => FLASH_PREFIX + window.location.pathname
// TrustCenterCampaign::OPT_IN_SCREENSHOT_CONTENT_TYPES; the server validates too.
const ACCEPTED_IMAGES = "image/png,image/jpeg,image/webp"

export default class extends Controller {
  static targets = ["button", "message"]

  connect() {
    this.busy = false
    this.showStoredFlash()
  }

  async run(event) {
    event.preventDefault()
    const button = event.currentTarget
    if (this.busy || button.disabled) return

    const { url, confirmTitle, confirmMessage, confirmLabel } = button.dataset
    const upload = button.dataset.upload === "true"
    const file = upload ? await this.pickFile() : null
    if (upload && !file) return

    const message = file ? `${confirmMessage} File: ${file.name}` : confirmMessage
    const confirmed = await showConfirmModal(message, {
      title: confirmTitle,
      confirmText: confirmLabel,
      variant: "warning",
    })
    if (!confirmed) return

    this.busy = true
    this.setAllDisabled(true)
    setButtonLoading(button, true)
    try {
      const response = await post(url, { responseKind: "json", ...(file ? { body: this.formDataFor(file) } : {}) })
      const data = await this.readJson(response)
      if (response.ok) {
        this.storeFlash(data.message || "Done.")
        this.reload()
        return
      }
      this.showMessage(data.error || data.message || "The action did not run.", "danger")
    } catch (_error) {
      this.showMessage("Something went wrong. Please try again.", "danger")
    }
    setButtonLoading(button, false)
    this.setAllDisabled(false)
    this.busy = false
  }

  // ----- helpers ----------------------------------------------------------

  // Resolves with the chosen image, or null when the picker is cancelled.
  pickFile() {
    return new Promise((resolve) => {
      const input = document.createElement("input")
      input.type = "file"
      input.accept = ACCEPTED_IMAGES
      input.addEventListener("change", () => resolve(input.files?.[0] || null), { once: true })
      input.addEventListener("cancel", () => resolve(null), { once: true })
      input.click()
    })
  }

  formDataFor(file) {
    const body = new FormData()
    body.append("opt_in_screenshot", file)
    return body
  }

  async readJson(response) {
    try {
      return (await response.json) || {}
    } catch (_error) {
      return {}
    }
  }

  setAllDisabled(disabled) {
    this.buttonTargets.forEach((button) => {
      if (disabled) {
        button.dataset.wasDisabled = button.disabled ? "true" : "false"
        button.disabled = true
      } else {
        button.disabled = button.dataset.wasDisabled === "true"
        delete button.dataset.wasDisabled
      }
    })
  }

  showMessage(text, tone) {
    if (!this.hasMessageTarget) return
    const el = this.messageTarget
    el.classList.remove("d-none", "gl-alert--success", "gl-alert--danger")
    el.classList.add(`gl-alert--${tone}`)
    el.textContent = text
  }

  storeFlash(text) {
    try {
      window.sessionStorage.setItem(flashKey(), text)
    } catch (_error) {
      // Private mode: the reload still shows the new state, just not the message.
    }
  }

  showStoredFlash() {
    let text = null
    try {
      text = window.sessionStorage.getItem(flashKey())
      window.sessionStorage.removeItem(flashKey())
    } catch (_error) {
      return
    }
    if (text) this.showMessage(text, "success")
  }

  reload() {
    if (window.Turbo?.visit) {
      window.Turbo.visit(window.location.href, { action: "replace" })
    } else {
      window.location.reload()
    }
  }
}
